import { useState } from 'react';
import { FilterOptions } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

interface FilterPanelProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  onReset: () => void;
}

const FilterPanel = ({ filters, onFilterChange, onReset }: FilterPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const taxTypeOptions = [
    { value: 'all', label: 'Todos os Impostos' },
    { value: 'icms', label: 'ICMS' },
    { value: 'ipi', label: 'IPI' },
    { value: 'pis', label: 'PIS' },
    { value: 'cofins', label: 'COFINS' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'compliant', label: 'Conforme' },
    { value: 'warning', label: 'Atenção' },
    { value: 'error', label: 'Erro' }
  ];

  const ufOptions = [
    { value: '', label: 'Todas as UFs' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'PR', label: 'Paraná' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'BA', label: 'Bahia' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'CE', label: 'Ceará' }
  ];

  const handleTaxTypeChange = (value: string) => {
    onFilterChange({
      ...filters,
      taxType: value as FilterOptions['taxType']
    });
  };

  const handleStatusChange = (value: string) => {
    onFilterChange({
      ...filters,
      status: value as FilterOptions['status']
    });
  };

  const handleUfChange = (value: string) => {
    onFilterChange({
      ...filters,
      ufFilter: value
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      searchTerm: e.target.value
    });
  };

  const handleMinRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      rateRange: {
        ...filters.rateRange,
        min: parseFloat(e.target.value) || 0
      }
    });
  };

  const handleMaxRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      rateRange: {
        ...filters.rateRange,
        max: parseFloat(e.target.value) || 100
      }
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Filter" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Filtros</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Limpar
          </Button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-muted rounded-md transition-colors duration-150"
            aria-label={isExpanded ? 'Recolher filtros' : 'Expandir filtros'}
          >
            <Icon
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              color="var(--color-muted-foreground)"
            />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          type="search"
          placeholder="Buscar por código ou nome..."
          value={filters.searchTerm}
          onChange={handleSearchChange}
          className="w-full"
        />

        <Select
          options={taxTypeOptions}
          value={filters.taxType}
          onChange={handleTaxTypeChange}
          placeholder="Tipo de Imposto"
        />

        <Select
          options={statusOptions}
          value={filters.status}
          onChange={handleStatusChange}
          placeholder="Status"
        />

        <Select
          options={ufOptions}
          value={filters.ufFilter}
          onChange={handleUfChange}
          placeholder="UF"
        />
      </div>

      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Faixa de Alíquota (%)
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Mín"
                  value={filters.rateRange.min}
                  onChange={handleMinRateChange}
                  min={0}
                  max={100}
                  step={0.1}
                />
                <span className="text-muted-foreground">até</span>
                <Input
                  type="number"
                  placeholder="Máx"
                  value={filters.rateRange.max}
                  onChange={handleMaxRateChange}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;